import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Badge, Alert, CopyButton, Button, HoverCard, Avatar, Group, Stack, Text, TextInput, UnstyledButton, Tooltip } from '@mantine/core';
import { IconInfoCircle, IconBrandSteam, IconMail, IconPhone, IconCheck, IconCopy, IconExternalLink, IconChevronUp, IconChevronDown, IconSelector } from '@tabler/icons-react';
import { LanBooking, LanRegistration, LanSettingsPayload } from 'types/intra';
import {
    formatDate,
    formatAttendance,
    formatDevices,
    formatDiet,
    bookingLocationByEmail,
    getSeatForRegistration,
    displaySeatLocation,
    seatMatchesFilter,
    compareSeats,
    participantMatchesFilter,
    compareParticipants,
    cycleSortDirection,
    SortDirection
} from './intraUtils';
import intraService, { SteamUserResponse } from 'services/intraService';
import useAuth from 'hooks/useAuth';

/**
 * Props for the ProfileAvatar component.
 */
interface ProfileAvatarProps {
    /** The registration data containing participant information */
    registration: LanRegistration;
    /** Steam profile data if available, null otherwise */
    steamUser: SteamUserResponse | null;
}

/**
 * Displays a participant's avatar, using Steam profile picture if available.
 *
 * Falls back to initials from first and last name when Steam avatar
 * is unavailable or fails to load.
 *
 * @param props - The component props
 * @returns An avatar component with Steam image or initials
 */
const ProfileAvatar: React.FC<ProfileAvatarProps> = ({ registration, steamUser }) => {
    const [imgError, setImgError] = useState(false);
    const initials = `${registration.firstname?.[0] || ''}${registration.lastname?.[0] || ''}`.toUpperCase();

    return (
        <Avatar
            src={!imgError && steamUser?.avatarUrl ? steamUser.avatarUrl : null}
            alt={`${registration.firstname} ${registration.lastname}`}
            radius="xl"
            size="md"
            color="violet"
            onError={() => setImgError(true)}
            style={{ cursor: 'pointer' }}
        >
            {initials}
        </Avatar>
    );
};

/**
 * Props for the ProfileCard component.
 */
interface ProfileCardProps {
    /** The registration data containing participant information */
    registration: LanRegistration;
    /** Steam profile data if available, null otherwise */
    steamUser: SteamUserResponse | null;
}

/**
 * Displays a detailed participant profile card in a hover dropdown.
 *
 * Shows:
 * - Avatar with Steam profile picture or initials
 * - Full name and Steam persona name
 * - Copyable email and phone contact buttons
 * - Link to Steam profile
 * - Registration timestamp
 *
 * @param props - The component props
 * @returns A detailed profile card for hover display
 */
const ProfileCard: React.FC<ProfileCardProps> = ({ registration, steamUser }) => {
    const [imgError, setImgError] = useState(false);
    const initials = `${registration.firstname?.[0] || ''}${registration.lastname?.[0] || ''}`.toUpperCase();

    return (
        <Stack gap="sm">
            <Group>
                <Avatar
                    src={!imgError && steamUser?.avatarUrl ? steamUser.avatarUrl : null}
                    alt={`${registration.firstname} ${registration.lastname}`}
                    radius="xl"
                    size="lg"
                    color="violet"
                    onError={() => setImgError(true)}
                >
                    {initials}
                </Avatar>
                <div>
                    <Text fw={600} size="sm">
                        {registration.firstname} {registration.lastname}
                    </Text>
                    {steamUser?.personaName && steamUser.personaName !== registration.nickname && (
                        <Text size="xs" c="dimmed">Steam: {steamUser.personaName}</Text>
                    )}
                </div>
            </Group>

            <Stack gap={4}>
                {registration.email && (
                    <Group gap="xs">
                        <CopyButton value={registration.email} timeout={2000}>
                            {({ copied, copy }) => (

                                <Button color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}
                                    leftSection={<IconMail size={14} />}
                                    rightSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}>
                                    <Text size="xs" c="dimmed">{registration.email}</Text>
                                </Button>

                            )}
                        </CopyButton>
                    </Group>
                )}
                {registration.phone && (
                    <Group gap="xs">
                        <CopyButton value={registration.phone} timeout={2000}>
                            {({ copied, copy }) => (

                                <Button color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}
                                    leftSection={<IconPhone size={14} />}
                                    rightSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}>
                                    <Text size="xs" c="dimmed">{registration.phone}</Text>

                                </Button>

                            )}
                        </CopyButton>
                    </Group>
                )}
                {registration.steamId && (
                    <Group gap="xs">
                        
                        <Button
                            color="gray"
                            variant="subtle"
                            onClick={() => window.open(`https://steamcommunity.com/profiles/${registration.steamId}`, '_blank')}
                            leftSection={<IconBrandSteam size={14} />}
                            rightSection={<IconExternalLink size={12} />}>
                            {registration.nickname}
                        </Button>
                    </Group>
                )}
            </Stack>

            {registration.createdAt && (
                <Text size="xs" c="dimmed" ta="right">
                    Ilmoittautunut: {formatDate(registration.createdAt)}
                </Text>
            )}
        </Stack>
    );
};

/**
 * Props for the RegistrationsTable component.
 */
interface RegistrationsTableProps {
    /** Array of participant registration records to display */
    registrations: LanRegistration[];
    /** Table bookings used to resolve each registration's booked seat by email */
    bookings: LanBooking[];
    /** Current LAN event settings, affects column visibility */
    lanSettings: LanSettingsPayload | null;
    /** Function to check if an async operation is in progress for a given key */
    isOperating: (key: string) => boolean;
    /** Callback when registration history/archive status changes */
    onHistoryChange: (id: string, historyValue: number) => Promise<void>;
    /** Open the seat assign picker for the registration's booking */
    onProvisionClick: (booking: LanBooking) => void;
}

/** Wide enough for the "Paikka" label plus the sort icon; badges fill this width. */
const SEAT_COLUMN_STYLE: React.CSSProperties = {
    width: 'calc(6ch + 22px)',
    minWidth: 'calc(6ch + 22px)',
    maxWidth: 'calc(6ch + 22px)',
    paddingLeft: 6,
    paddingRight: 6,
    textAlign: 'center',
    boxSizing: 'content-box',
    whiteSpace: 'nowrap'
};

/** Hug avatar + nickname; 1% keeps auto-layout from growing this column. */
const PARTICIPANT_COLUMN_STYLE: React.CSSProperties = {
    width: '1%',
    whiteSpace: 'nowrap'
};

interface SortableFilterHeaderProps {
    label: string;
    sort: SortDirection | null;
    onCycleSort: () => void;
    sortAriaLabel: string;
    filter: string;
    onFilterChange: (value: string) => void;
    filterAriaLabel: string;
    compact?: boolean;
}

const sortIconFor = (sort: SortDirection | null) => {
    if (sort === 'asc') {
        return <IconChevronUp size={14} />;
    }
    if (sort === 'desc') {
        return <IconChevronDown size={14} />;
    }
    return <IconSelector size={14} />;
};

const SortableFilterHeader: React.FC<SortableFilterHeaderProps> = ({
    label,
    sort,
    onCycleSort,
    sortAriaLabel,
    filter,
    onFilterChange,
    filterAriaLabel,
    compact
}) => {
    const button = (
        <UnstyledButton
            onClick={onCycleSort}
            style={{ display: 'flex', alignItems: 'center', justifyContent: compact ? 'center' : undefined, gap: 4, whiteSpace: 'nowrap' }}
            aria-label={sortAriaLabel}
        >
            {!compact && (
                <Text size="sm" fw={700} span>{label}</Text>
            )}
            {sortIconFor(sort)}
        </UnstyledButton>
    );

    return (
        <Stack gap={4} align={compact ? 'center' : 'stretch'}>
            {compact ? (
                <Tooltip label={label} withArrow>{button}</Tooltip>
            ) : (
                button
            )}
            <TextInput
                size="xs"
                placeholder={compact ? undefined : 'Suodata…'}
                value={filter}
                onChange={event => onFilterChange(event.currentTarget.value)}
                aria-label={filterAriaLabel}
                w="100%"
                miw={0}
                styles={{
                    input: {
                        minHeight: 22,
                        width: 0,
                        minWidth: '100%',
                        maxWidth: '100%',
                        paddingInline: 4,
                        textAlign: compact ? 'center' : undefined
                    }
                }}
            />
        </Stack>
    );
};

/**
 * Displays a table of LAN event participant registrations.
 *
 * Features include:
 * - Participant avatars with Steam profile integration on hover
 * - Nickname badges with hover cards showing full profile details
 * - Participant column (sortable and filterable by name, nickname, email, phone)
 * - Booked seat (sortable and filterable), joined from bookings by email
 * - Attendance days (conditional on lanSettings.attendancePerDayEnabled)
 * - Device type icons (PC, console, other)
 * - Diet preferences with food order status (conditional on lanSettings.foodEnabled)
 * - Additional notes/feedback
 *
 * Automatically fetches Steam profile data for participants with Steam IDs.
 *
 * @param props - The component props
 * @returns A scrollable table of registrations or an info alert if empty
 */
const RegistrationsTable: React.FC<RegistrationsTableProps> = ({
    registrations,
    bookings,
    lanSettings,
    onProvisionClick
}) => {
    const { accessToken } = useAuth();
    const [steamUsers, setSteamUsers] = useState<Record<string, SteamUserResponse | null>>({});
    const [participantFilter, setParticipantFilter] = useState('');
    const [participantSort, setParticipantSort] = useState<SortDirection | null>(null);
    const [seatFilter, setSeatFilter] = useState('');
    const [seatSort, setSeatSort] = useState<SortDirection | null>(null);

    const locationsByEmail = useMemo(() => bookingLocationByEmail(bookings), [bookings]);

    const displayedRegistrations = useMemo(() => {
        const filtered = registrations.filter(registration =>
            participantMatchesFilter(registration, participantFilter) &&
            seatMatchesFilter(getSeatForRegistration(registration, locationsByEmail), seatFilter)
        );
        if (participantSort) {
            return [...filtered].sort((a, b) => compareParticipants(a, b, participantSort));
        }
        if (seatSort) {
            return [...filtered].sort((a, b) =>
                compareSeats(
                    getSeatForRegistration(a, locationsByEmail),
                    getSeatForRegistration(b, locationsByEmail),
                    seatSort
                )
            );
        }
        return filtered;
    }, [registrations, locationsByEmail, participantFilter, seatFilter, participantSort, seatSort]);

    const columnCount =
        4 +
        (lanSettings?.attendancePerDayEnabled ? 1 : 0) +
        (lanSettings?.foodEnabled !== false ? 1 : 0);

    const cycleParticipantSort = () => {
        setSeatSort(null);
        setParticipantSort(cycleSortDirection);
    };

    const cycleSeatSort = () => {
        setParticipantSort(null);
        setSeatSort(cycleSortDirection);
    };

    const fetchSteamUsers = useCallback(async () => {
        const steamIds = registrations
            .map(r => r.steamId)
            .filter((id): id is string => Boolean(id));

        if (steamIds.length === 0) return;

        const results = await Promise.all(
            steamIds.map(async (steamId) => {
                // Skip if already fetched
                if (steamUsers[steamId] !== undefined) return null;
                const user = await intraService.fetchSteamUser(steamId, accessToken ?? undefined);
                return { steamId, user };
            })
        );

        const newUsers: Record<string, SteamUserResponse | null> = {};
        results.forEach(result => {
            if (result) {
                newUsers[result.steamId] = result.user;
            }
        });

        if (Object.keys(newUsers).length > 0) {
            setSteamUsers(prev => ({ ...prev, ...newUsers }));
        }
    }, [registrations, accessToken, steamUsers]);

    useEffect(() => {
        fetchSteamUsers();
    }, [registrations, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!registrations.length) {
        return <Alert icon={<IconInfoCircle size={16} />} color="blue">Ei ilmoittautumisia juuri nyt.</Alert>;
    }

    return (
        <div>
            <Table.ScrollContainer minWidth={500} type="native">
                <Table
                    highlightOnHover
                    withTableBorder
                    withColumnBorders
                    stickyHeader
                    stickyHeaderOffset={0}
                    styles={{
                        th: {
                            backgroundColor: 'white',
                            verticalAlign: 'top'
                        },
                        td: {
                            verticalAlign: 'middle'
                        }
                    }}
                >
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={PARTICIPANT_COLUMN_STYLE}>
                                <SortableFilterHeader
                                    label="Osallistuja"
                                    sort={participantSort}
                                    onCycleSort={cycleParticipantSort}
                                    sortAriaLabel="Järjestä osallistujan mukaan"
                                    filter={participantFilter}
                                    onFilterChange={setParticipantFilter}
                                    filterAriaLabel="Suodata osallistuja"
                                />
                            </Table.Th>
                            <Table.Th style={SEAT_COLUMN_STYLE}>
                                <SortableFilterHeader
                                    label="Paikka"
                                    sort={seatSort}
                                    onCycleSort={cycleSeatSort}
                                    sortAriaLabel="Järjestä paikan mukaan"
                                    filter={seatFilter}
                                    onFilterChange={setSeatFilter}
                                    filterAriaLabel="Suodata paikka"
                                />
                            </Table.Th>
                            {lanSettings?.attendancePerDayEnabled && (
                                <Table.Th>Osallistuminen</Table.Th>
                            )}
                            <Table.Th>Laitteet</Table.Th>
                            {lanSettings?.foodEnabled !== false && (
                                <Table.Th>Ruokailu</Table.Th>
                            )}
                            <Table.Th>Muistiinpanot</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {displayedRegistrations.length === 0 ? (
                            <Table.Tr>
                                <Table.Td colSpan={columnCount}>
                                    <Alert icon={<IconInfoCircle size={16} />} color="blue">
                                        Ei osumia annetuilla suodattimilla.
                                    </Alert>
                                </Table.Td>
                            </Table.Tr>
                        ) : displayedRegistrations.map(registration => {

                            return (
                                <Table.Tr key={registration.id}>
                                    <Table.Td style={PARTICIPANT_COLUMN_STYLE}>
                                        <Group gap="sm" wrap="nowrap">
                                            <HoverCard width={320} shadow="md" withArrow openDelay={200} closeDelay={100}>
                                                <HoverCard.Target>
                                                    <div>
                                                        <ProfileAvatar registration={registration} steamUser={registration.steamId ? steamUsers[registration.steamId] ?? null : null} />
                                                    </div>
                                                </HoverCard.Target>
                                                <HoverCard.Dropdown>
                                                    <ProfileCard registration={registration} steamUser={registration.steamId ? steamUsers[registration.steamId] ?? null : null} />
                                                </HoverCard.Dropdown>
                                            </HoverCard>
                                            {registration.nickname && (
                                                <Badge color="violet.4" variant="light" size="xs" fullWidth radius="md">
                                                    {registration.nickname}
                                                </Badge>
                                            )}

                                        </Group>
                                    </Table.Td>
                                    <Table.Td style={SEAT_COLUMN_STYLE}>
                                        <Badge
                                            color="violet.4"
                                            size="xs"
                                            radius="sm"
                                            fullWidth
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                const booking = bookings.find(
                                                    item => item.email?.toLowerCase() === registration.email?.toLowerCase()
                                                );
                                                if (booking) {
                                                    onProvisionClick(booking);
                                                }
                                            }}
                                        >
                                            {displaySeatLocation(getSeatForRegistration(registration, locationsByEmail))}
                                        </Badge>
                                    </Table.Td>
                                    {lanSettings?.attendancePerDayEnabled && (
                                        <Table.Td>{formatAttendance(registration)}</Table.Td>
                                    )}
                                    <Table.Td>{formatDevices(registration)}</Table.Td>
                                    {lanSettings?.foodEnabled !== false && (
                                        <Table.Td>{formatDiet(registration)}</Table.Td>
                                    )}
                                    <Table.Td>{registration.feedback || '-'}</Table.Td>

                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
        </div>
    );
};

export default RegistrationsTable;

